/**
 * ツールバー (デザイン .ecl-toolbar 準拠)。
 *
 * フィルタグループを design 順で横並び wrap 配置:
 *   対象 | 取込 (簡易予測) | 調整 (最大電力補正) | 過去実績 (類似日から選択 … 確定履歴から選択)
 *
 * spec 準拠改修 (demand-forecast-daily-v4) で以下を削除した:
 * - AC-08: 「予測方法」select 単独露出 (簡易予測は「取込:」グループのメニューへ統合)
 * - AC-07: Toolbar 想定履歴 chips グループ (表示/非表示トグル UI)
 * - AC-27: 類似日ダイアログの呼び出しを 1 ボタン化 (旧「過去実績 > ＋追加 >
 *   類似日から選択」の二段構造を置き換え)。 ボタンは 8080 と同じく過去実績
 *   グループ内・「確定履歴から選択」の左に置く (2026-08-25 デザイナー指摘)
 *
 * 「確定履歴から選択」ボタン (AC-DFD-029, design 決定 2026-07-15): 旧「取込:」
 * グループを離れ、過去実績グループの右端にラベル無し単体ボタンとして配置する
 * (design demand/forecast-today.html:1021 #dft-histimport-btn / margin-left:auto
 * wrap)。「取込:」グループは簡易予測メニューのみが残る。
 *
 * 「調整: 最大電力補正」ボタン (RescaleModal) は当初 AC-08 の一部として削除したが、
 * 共通仕様 demand-forecast-common.md AC-DFC-EDIT-001 (SHALL) の見落としと判明し復元
 * 済み (spec 準拠)。
 *
 * 業務エリアグループは design 準拠で非表示:
 * - この画面は caseId 必須で業務エリアはケースに固定のため、design L1764-1770
 *   準拠で filter group ごとレンダリングしない。代わりに対象日ラベル
 *   (SectionHeader) に「YYYY/MM/DD (曜) エリア名」形式で表示する。
 *
 * プロバイダ切替チップ (spec AC-DFC-001/004、AC-07): 過去に一度 design 未反映を
 * 理由として復元したが、spec 準拠調整 (demand-forecast-daily-spec-align)
 * AC-07 で「予測プロバイダ切替」機能自体 (Toolbar チップ + KomaGrid 予測列 +
 * ForecastChart 系列) を削除する意思決定がなされたため撤去した。backend/proto の
 * `available_providers` / `provider_forecasts` / `selected_providers` は
 * #4072 (provider stub 撤去) で proto ごと削除済み
 * (Phase 2 外部プロバイダ契約は別 map で再設計)。
 *
 * design 参照: design/demand/forecast-today.html .ecl-toolbar
 */
import type { RetailerDraft } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  Flex,
  HStack,
  MenuRoot,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  TagCloseTrigger,
  TagLabel,
  TagRoot,
  TertiaryButton,
  Text,
  TextButton,
  Tooltip,
  Wrap,
  WrapItem,
} from '@enechain/ecloud-designsystem'
import { FaChevronDownRegular } from '@enechain/ecloud-icons'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import {
  DsSelect,
  type DsSelectOptions,
  flattenDsSelectOptions,
} from '~/components/common/DsSelect'
import { iconSize } from '~/constants/iconSize'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import type { TargetScope } from '~/pages/DemandForecastDaily/models/types'

/**
 * フィルタグループのラベル (太字・高視認色・折返し無し) 共通表示。
 * 各グループ共通のスタイルを 1 箇所に集約する (7 グループで反復していた Text props の重複除去)。
 *
 * 縦位置は各グループの HStack が `alignItems="center"` (= HStack 既定) で揃える。
 * design 実測 (8080 `css/components.css` の `.filter-group`) が `align-items: center`
 * なので、ラベル側で padding を足して位置合わせしない。
 */
/**
 * chakra v3 の Menu 部品の props 型は本プロジェクトの型解決 (ecloud 0.14 / chakra
 * 3.29 の d.ts 解決問題) で `HTMLChakraProps` が collapse するため、v3 本来の
 * シグネチャへ cast する (MasterTableActionBar と同型)。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; disabled?: boolean }
>

const FilterLabel = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  <Text textStyle="filterLabel" color="Text.highEmphasis" whiteSpace="nowrap">
    {children}
  </Text>
)

/**
 * 過去実績グループ (design L690-717): chips 常時表示 + 追加/削除/クリア。
 * AC-27 の「類似日から選択」導線 (Props.onSimilarDaySearchOpen) も 8080 と同じく
 * このグループ内、 chips の直後に置く (2026-08-25 デザイナー指摘)。
 *
 * 確定履歴の参照列 chips (s07-confirm-history-import-reference-view.md
 * Decision #2/#5) も同じ UI 領域に表示する: 過去実績と確定履歴は独立の
 * state だが、chip 表示・「クリア」ボタンの UX は共通にする (design pattern)。
 */
type PastActualProps = {
  /** 選択済み日付 (YYYY-MM-DD[])。design L690 chips 常時表示 */
  dates: string[]
  /** chip 単位で解除 (design L717 の chip 削除相当) */
  onRemove: (date: string) => void
  /**
   * chip の日付を**その場で選び直す** (デザイナー指摘 2026-09-14「8080 同様、
   * calendar を再選択できる UI にして、 X ボタンを付与」)。
   *
   * 8080 (`forecast-today.html` `.dft-slot-chip`) は各スロットを date input +
   * 削除ボタンで組んでおり、 選択済みの日付を消さずに差し替えられる。 実装は
   * 表示専用の Tag だったため、 1 日ずらすだけでも「× で消す → 類似日ダイアログを
   * 開く → 選び直す」 の 3 手が要っていた。
   *
   * **位置は保つ** (`from` を `to` で置換する)。 チャートの系列色・48 コマ表の
   * 過去実績列は配列順 (`past_0`..`past_4`) に対応しているので、 削除 + 末尾追加に
   * すると選び直しただけで色と列順が変わる。
   */
  onDateChange: (from: string, to: string) => void
  /** 「クリア」で過去実績 + 確定履歴の参照列 state をまとめて空にする (Decision #5) */
  onClear: () => void
  /** 確定履歴参照列 chips (id + 「確定 MM/DD」ラベル) */
  confirmRecordRefs: Array<{ id: string; label: string }>
  /** 確定履歴参照列 chip の × クリックで 1 件解除 */
  onConfirmRecordRefRemove: (id: string) => void
}

/** §6 取込:グループ内の予測方法メニュー (AC-033/035) */
type SimpleForecastProps = {
  /**
   * 予測方法 select の現在値 (option value 文字列)。
   * 呼び出し元が確定/キャンセル後に空文字へリセットする (design の
   * 「キャンセル時は選択を元に戻す」相当)。
   */
  methodValue: string
  /** 予測方法選択 (§6 手法選択 UI の起点、AC-033/035)。メニュー項目クリックで呼ぶ */
  onMethodChange: (value: string) => void
}

/**
 * §5 確定履歴から取込ボタンの活性制御。ボタン自体は「取込:」グループではなく
 * 過去実績グループの右端に単体配置する (design 決定 2026-07-15、上記
 * PastActualProps 直前のコメント参照)。
 */
type ImportActionsProps = {
  /**
   * §5 確定履歴から取込ボタンの活性条件: 小売ビューで編集可のときのみ活性、
   * BG 全体ビューは常時活性 (呼び出し元 index.tsx の canImportForecast 参照)。
   * 閲覧者/読み取り専用ケースは currentEditRow 不在または isCompleted で表現
   * される。
   *
   * 2026-08-13 (demand-forecast-daily-bg-total-import-tooltip): §6 簡易予測
   * ボタンの活性判定にはもう使わない (Decision #4)。§6 の disable 条件
   * (isBgTotalScope) は Toolbar が既に受けている `targetScope` から局所派生
   * する — index.tsx 側で重複算出して prop で渡さない (Decision #5、
   * code-quality-reviewer 指摘の Feature Envy / Duplicated derivation 対応)。
   */
  canImport: boolean
  /** §5 確定履歴から取込ボタン */
  onConfirmRecordImportOpen: () => void
}

type Props = {
  targetScope: TargetScope
  selectedRetailerBusinessCd: string | undefined
  onTargetScopeChange: (scope: TargetScope) => void
  onRetailerChange: (retailerBusinessCd: string) => void

  retailers: RetailerDraft[]

  pastActual: PastActualProps

  /**
   * AC-27 (spec §4 冒頭): 類似日ダイアログの呼び出しを 1 ボタンに集約する
   * (旧「過去実績 > ＋追加 > 類似日から選択」の二段構造を置き換え)。
   */
  onSimilarDaySearchOpen: () => void

  simpleForecast: SimpleForecastProps
  importActions: ImportActionsProps

  /**
   * 「最大電力補正」活性化条件 (共通仕様 demand-forecast-common.md
   * AC-DFC-EDIT-001): 小売選択中かつ未完了のときのみ。
   */
  canRescale: boolean
  /** 「最大電力補正」モーダルを開く (design L872-896) */
  onRescaleOpen: () => void
  /**
   * デザイナー指摘 (2026-08-13): 「調整」ボタンが disabled のとき、なぜ押せないかを
   * hover tooltip で示す。呼び出し元 (index.tsx) が `canRescale` と同じ 4 条件から
   * `models/toolbarGuards.ts` の `adjustmentDisabledReasonKey` で導出した理由文言。
   * `canRescale=true` (= 活性) のときは undefined (tooltip 非表示)。
   */
  adjustmentDisabledReason?: string
}

export const Toolbar = ({
  targetScope,
  selectedRetailerBusinessCd,
  onTargetScopeChange,
  onRetailerChange,
  retailers,
  pastActual,
  onSimilarDaySearchOpen,
  simpleForecast,
  importActions,
  canRescale,
  onRescaleOpen,
  adjustmentDisabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const targetSelectValue =
    targetScope === 'BG_TOTAL'
      ? 'BG_TOTAL'
      : targetScope === 'UNATTRIBUTED'
        ? 'UNATTRIBUTED'
        : (selectedRetailerBusinessCd ?? 'BG_TOTAL')

  const handleTargetChange = (value: string): void => {
    if (value === 'BG_TOTAL') {
      onTargetScopeChange('BG_TOTAL')
    } else if (value === 'UNATTRIBUTED') {
      onTargetScopeChange('UNATTRIBUTED')
    } else {
      onRetailerChange(value)
    }
  }

  // AC-DFD-033/033a (Decision #5、code-quality-reviewer 指摘対応): §6 簡易予測
  // 一括取込ボタンの disable gate である BG_TOTAL 判定は、Toolbar が既に
  // targetSelectValue 算出等で消費している `targetScope` から局所派生する
  // (index.tsx 側では算出せず prop としても渡さない。重複算出の解消)。
  // §6 簡易予測ボタンのラベル (BG_TOTAL disable 分岐・活性 Menu 分岐で共通)
  const simpleForecastButtonLabel = (
    <HStack gap={1}>
      <span>{t('page:demandForecastDaily.toolbar.simpleForecastButton')}</span>
      {/* ラベル付きボタンの icon は 20px (UI 辞書 icons.md)。 指定が無いと DS 既定の 24px */}
      <FaChevronDownRegular boxSize={iconSize.labeledButton} />
    </HStack>
  )

  // AC-DFD-033a: BG 全体も、backend が返す内訳別 preview を atomic に取り込む。
  // 編集可否の gate は小売表示と同じ importActions に集約する。
  const simpleForecastDisabled = !importActions.canImport
  const simpleForecastButton = (
    // [rule 逸脱] toolbar 主導線は design 実測 40px (下記コメント参照)。size 省略 = md
    <TertiaryButton disabled={simpleForecastDisabled}>
      {simpleForecastButtonLabel}
    </TertiaryButton>
  )

  // §6 簡易予測の手法のうち選べるもの (表示順は §1 マトリクス)。
  const simpleForecastMethodItems = [
    {
      value: 'simple_copy',
      label: t('page:demandForecastDaily.toolbar.forecastMethodSimpleCopy'),
    },
    {
      value: 'same_weekday_category_copy',
      label: t(
        'page:demandForecastDaily.toolbar.forecastMethodSameWeekdayCategoryCopy',
      ),
    },
    {
      value: 'weighted_avg',
      label: t('page:demandForecastDaily.toolbar.forecastMethodWeightedAvg'),
    },
    {
      value: 'same_weekday_category_weighted_avg',
      label: t(
        'page:demandForecastDaily.toolbar.forecastMethodSameWeekdayCategoryWeightedAvg',
      ),
    },
  ]

  // 対象 select の候補 (先頭に BG全体、続けて小売グループ)。
  // options と幅決め sizer (下記 JSX コメント参照) の両方が同じ配列を見る。
  const targetSelectOptions: DsSelectOptions = [
    {
      value: 'BG_TOTAL',
      label: t('page:demandForecastDaily.toolbar.bgTotal'),
    },
    {
      label: t('page:demandForecastDaily.toolbar.retailerGroup'),
      options: retailers.map((r) => ({
        value: r.retailerBusinessCd,
        label: r.displayName,
      })),
    },
    {
      label: t(
        'page:demandForecastDaily.toolbar.unidentifiedBusinessCodeGroup',
      ),
      options: [
        {
          value: 'UNATTRIBUTED',
          label: t('page:demandForecastDaily.toolbar.unattributed'),
        },
      ],
    },
  ]

  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      rowGap={4}
      columnGap={5}
      marginBottom={4}
      padding={3}
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      // design 実測 (8080 `.ecl-toolbar`, css/components.css:4129-4144):
      // background: var(--surface-primary) が抜けていた。他の値 (padding /
      // border / gap 16px 20px = rowGap4/columnGap5 相当) は既に一致していたため
      // 追加のみ。FilterToolbar (共通部品) への置換は外側 margin が 0 固定で、
      // 本画面は marginBottom={4} が Stack gap=0 配下の唯一の縦間隔源のため見送り
      // (置換すると次カードとの間隔が潰れる)。
      backgroundColor="Surface.primary"
    >
      {/* 対象: BG全体 / 小売 */}
      {/* design 実測 (8080 `.ecl-toolbar > .filter-group { min-height: 40px }`) */}
      <HStack flexShrink={0} gap={2} minHeight="40px">
        <FilterLabel>
          {t('page:demandForecastDaily.toolbar.target')}
        </FilterLabel>
        {/* 幅は固定・選択前後で動かさない (2026-08-19 デザイナー指摘: 全画面共通)。
            固定幅の値は 160px (= 8080 の値) ではなく「候補の最長ラベルが収まる幅」
            にする (2026-08-31 デザイナー指摘: 選択値を「...」で見切れさせない。
            実データの小売名は 8080 のデモ名 (ソラパワー等) より長くなり得て、
            160px 固定では ellipsis になる)。 全候補ラベルを height 0 の sizer 行
            として敷き、 wrapper (width: fit-content) の幅 = 最長ラベル + 余白 で
            固定する。 余白 (paddingInline) は DS Select の value 余白 + indicator
            の近似で実余白より広め (不足による見切れ防止が目的)。 ラベル文字は
            ::before の content: attr() で描き、 DOM の textContent には乗せない
            (= 文字列で引くテスト・読み上げに候補名を二重に見せない)。
            font は token md (= 14px、 selectFontFix が当てる閉じた状態の実 font) に合わせる。
            size は既定 (md = 40px)。 8080 の `.ecl-select--sm` は名前に反して
            height 40px (`design/css/components.css:3181`) で、 chakra の
            `size="sm"` = 36px は 1 段小さかった (同じ toolbar の TertiaryButton と
            `.filter-group { min-height: 40px }` から 4px ずれる)。
            可視ラベル (FilterLabel) は `<Text>` で `<label for>` を張れないため
            `aria-label` で読み上げ名を与える (8080 も同 select に
            `aria-label="対象"` を置いている)。 */}
        <Box width="fit-content" minWidth="160px">
          {flattenDsSelectOptions(targetSelectOptions).map((o) => (
            <Box
              key={o.value}
              overflow="hidden"
              height={0}
              fontSize="md"
              _before={{ content: 'attr(data-label)' }}
              whiteSpace="nowrap"
              aria-hidden
              data-label={o.label}
              data-select-sizer
              paddingInlineEnd="52px"
              paddingInlineStart="12px"
            />
          ))}
          <DsSelect
            width="100%"
            aria-label={t('page:demandForecastDaily.toolbar.target')}
            onChange={(v) => {
              handleTargetChange(v ?? 'BG_TOTAL')
            }}
            options={targetSelectOptions}
            value={targetSelectValue}
          />
        </Box>
      </HStack>

      {/* 取込: 簡易予測から取込 (§6, AC-033/035、AC-08: 「予測方法」select
          単独露出は廃止しメニュー化)。「週間計画から取込」は Phase 1 に週次断面が
          無いため未実装 (design doc §9 R3)。確定履歴から取込ボタンは design 決定
          (2026-07-15) により過去実績グループの右端へ移設済み (下記
          #dft-histimport-btn 相当、design demand/forecast-today.html:1021)。
          活性条件 (importActions.canImport, 呼び出し元 index.tsx が算出): 小売
          ビューは編集可のときのみ活性。
          AC-DFD-033/033a (Decision #1/#2、demand-forecast-daily-bg-total-import-
          取込可否は `simpleForecastDisabled` (`!canImport`) に統一する。
          にする。RETAILER でも disabled のとき (完了 / 過去日 / 小売未選択)
          Menu を描画すると、ecloud の `TertiaryButton` は native disabled ではなく
          aria-disabled で非活性を表現するため Menu trigger のクリックで開いて
          しまい、手法選択 → BG_TOTAL 以外の非活性ケースでも PreviewSimpleForecast
          に到達する経路が残る (レビュー #3780358582)。tooltip 文言は BG_TOTAL
          専用 (spec AC-DFD-033a) のため、RETAILER 側の disabled では Tooltip は
          出さず disabled button のみを描画する。
          Tooltip は `<Tooltip><Menu>...</Menu></Tooltip>` にはしない: DS `Menu` は
          asChild で子要素に trigger 属性をクローンするだけで、追加の DOM
          イベント props (`onPointerEnter` 等) を自身の内部 button まで転送しない
          ため hover が発火しない (userEvent.hover で probe 検証済み)。Tooltip は
          素の span 経由でボタンへかぶせる — disabled な `<button>` は pointer
          event を発火しないため、Tooltip の子として直接置くと hover が起動しない
          (`SendErrorReasonTooltip` と同型)。 */}
      {/* design 実測 (8080 `.ecl-toolbar > .filter-group { min-height: 40px }`) */}
      <HStack flexShrink={0} gap={2} minHeight="40px">
        <FilterLabel>
          {t('page:demandForecastDaily.toolbar.importGroup')}
        </FilterLabel>
        {simpleForecastDisabled ? (
          simpleForecastButton
        ) : (
          // AC-DFD-061 (#10818): 取得元の需要想定 週間 (P1) を提供するまで、
          // 「週間計画コピー」を出したまま選べない状態にして理由を示す。選択肢には
          // ツールチップを出せないため、理由は選択肢名の下に小さく添える (Des 決定
          // #10919、prototype の ecl-select data-reason と同じ形)。DS `Menu` の項目は
          // label が文字列だけで 2 行にできないため、DS `Menu` と同じ構成を部品で組む。
          // 対象日で出し分けない点は AC-DFM-001。
          <div>
            <MenuRoot>
              <MenuTrigger asChild>{simpleForecastButton}</MenuTrigger>
              <MenuPositioner>
                <MenuContent overflowY="auto" maxHeight="400px">
                  {simpleForecastMethodItems.map((item) => (
                    <MenuItem
                      key={item.value}
                      onClick={() => {
                        simpleForecast.onMethodChange(item.value)
                      }}
                      value={item.value}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                  <MenuItem
                    alignItems="flex-start"
                    flexDirection="column"
                    gap={0.5}
                    disabled
                    value="upper_section_copy"
                  >
                    <Text>
                      {t(
                        'page:demandForecastDaily.toolbar.forecastMethodUpperSectionCopy',
                      )}
                    </Text>
                    <Text textStyle="menuItemDesc" color="Text.midEmphasis">
                      {t(
                        'page:demandForecastDaily.toolbar.forecastMethodNotReadyReason',
                      )}
                    </Text>
                  </MenuItem>
                </MenuContent>
              </MenuPositioner>
            </MenuRoot>
          </div>
        )}
      </HStack>

      {/* 調整: 最大電力補正 (design L570 / L872-896、共通仕様 AC-DFC-EDIT-001)。
          AC-01/AC-08 の削除は共通仕様の見落としだったため復元 (spec 準拠)。 */}
      {/* design 実測 (8080 `.ecl-toolbar > .filter-group { min-height: 40px }`) */}
      <HStack flexShrink={0} gap={2} minHeight="40px">
        <FilterLabel>
          {t('page:demandForecastDaily.toolbar.adjustment')}
        </FilterLabel>
        {/* [rule 逸脱] toolbar 主導線は design 実測 40px (上記コメント参照)。size 省略 = md */}
        {/* デザイナー指摘 (2026-08-13): disabled 理由を hover tooltip で示す。
            ecloud TertiaryButton の disabled は native `disabled` 属性ではなく
            aria-disabled (`de` 内部実装、onClick を preventDefault/stopPropagation
            で無効化するだけ) のため pointer events 自体は塞がれないが、
            `ConfirmRecordImportDialog` の「取込」ボタンと同様に直接 Tooltip で
            wrap すると hover で表示できることを確認済み。ただし `<label>` を持たない
            単一ボタンのため `WorkflowCaseDetail/SubStepRow` と同じ `<span>` 一枚で
            wrap する防御的パターンを踏襲する (disabled な button 実装が将来
            native disabled に変わっても hover を失わないため)。 */}
        {adjustmentDisabledReason !== undefined ? (
          <Tooltip content={adjustmentDisabledReason} placement="top" showArrow>
            <span>
              <TertiaryButton disabled={!canRescale} onClick={onRescaleOpen}>
                {t('page:demandForecastDaily.toolbar.adjustMaxPower')}
              </TertiaryButton>
            </span>
          </Tooltip>
        ) : (
          <TertiaryButton disabled={!canRescale} onClick={onRescaleOpen}>
            {t('page:demandForecastDaily.toolbar.adjustMaxPower')}
          </TertiaryButton>
        )}
      </HStack>

      {/* 過去実績: chips 常時表示 (design L690) + ＋追加 (カレンダーで指定、design
          L707-712) + クリア (design L717)。AC-27 で類似日検索は上記トップレベル
          ボタンへ移設済みのため単一導線 (メニュー不要) に簡素化。 */}
      {/* design 実測 (8080 `.ecl-toolbar > .filter-group { min-height: 40px }`) */}
      <HStack flexShrink={0} gap={2} minHeight="40px">
        <FilterLabel>
          {t('page:demandForecastDaily.toolbar.pastActual')}
        </FilterLabel>
        <Flex alignItems="center" flexWrap="wrap" gap={2}>
          {pastActual.dates.length > 0 && (
            <Wrap gap={2}>
              {pastActual.dates.map((date, idx) => (
                <WrapItem key={date}>
                  {/* 幅は共通の日付欄の幅 (曜日付きの値が収まる幅)。 元は 8080
                      (`.dft-slot-chip .ecl-date-input`) の実測 150px で、 同画面の類似日
                      ダイアログ「日付を手動追加」 と揃えている。
                      `max` は付けない — 同画面の手動追加も無制限で、 AC-DFD-022 は
                      「遡る日数」 の候補範囲外の日も追加できると定めている。 */}
                  <Box flexShrink={0} width={COMPACT_DATE_FIELD_WIDTH}>
                    <CompactCivilDateField
                      aria-label={t(
                        'page:demandForecastDaily.toolbar.pastActualDateField',
                        { index: idx + 1 },
                      )}
                      hasClear
                      id={`past-actual-date-${date}`}
                      onChange={(d) => {
                        // × / キーボードでの空欄化 = そのスロットを外す
                        if (d === undefined) {
                          pastActual.onRemove(date)
                          return
                        }
                        const next = civilDateToInputValue(d)
                        if (next === date) return
                        pastActual.onDateChange(date, next)
                      }}
                      value={parseDateInputValue(date)}
                    />
                  </Box>
                </WrapItem>
              ))}
            </Wrap>
          )}
          {/* 確定履歴参照列 chips (s07-confirm-history-import-reference-view.md
              Decision #2/#5): 過去実績と同じ領域に、左に境界線で区別して表示する
              (design demand/forecast-today.html L391「確定履歴列は... 左に境界線」)。 */}
          {pastActual.confirmRecordRefs.length > 0 && (
            <Wrap
              gap={2}
              paddingLeft={2}
              borderLeftWidth="1px"
              borderLeftColor="Border.lowEmphasis"
            >
              {pastActual.confirmRecordRefs.map((ref) => (
                <WrapItem key={ref.id}>
                  <TagRoot colorPalette="cyan" size="md" variant="subtle">
                    <TagLabel>{ref.label}</TagLabel>
                    <TagCloseTrigger
                      aria-label={t(
                        'page:demandForecastDaily.toolbar.confirmRecordRefChipRemove',
                        { label: ref.label },
                      )}
                      onClick={() => {
                        pastActual.onConfirmRecordRefRemove(ref.id)
                      }}
                    />
                  </TagRoot>
                </WrapItem>
              ))}
            </Wrap>
          )}
          {/* 「＋ 追加」ボタン (押下で日付 Input を出す「カレンダーで指定」導線) は
              削除した (デザイナー指摘 2026-08-14)。 design の過去実績グループ
              (`design/demand/forecast-today.html` L1000-1024) に対応要素が無く、
              8080 側は 2026-07-15 に「カレンダーで指定」を廃止して手動日付追加を
              類似日ダイアログ内の「日付を手動追加」へ集約済みだったため、 実装だけが
              旧導線を残していた。 手動指定の手段は `SimilarDayDialog` の「任意日追加」
              が引き続き提供する (= 機能の喪失は無い)。

              なお main 側 (PR #5323) はこの導線を「存続可否はデザイナー判断待ち」と
              して残したまま日付 widget だけを `CompactCivilDateField` へ DS 化したが、
              その判断が本指摘で「削除」に確定したため、 DS 化ごと落とす。 */}
          {/* 「類似日から選択」(design demand/forecast-today.html:1040
              #dft-similar-search-btn)。 8080 と同じく**過去実績グループの中**、
              chips の直後 = 右寄せされる「確定履歴から取込」 の左に置く
              (2026-08-25 デザイナー指摘)。 以前は AC-27 の実装として独立した
              トップレベルグループに出していたが、 8080 では一貫して過去実績
              グループ内の導線なので揃える (AC-27 の「二段構造をやめて 1 ボタンに
              する」 という意図自体は変わらない)。

              [rule 逸脱] ページ主操作ではないが size を明示しない。 design 実測
              (8080 `.ecl-toolbar`) では主導線は
              `btn btn-tertiary` = **40px**。 design を正とし size を省略する
              (= ecloud default md = 40px)。

              アイコンは付けない (2026-08-25 デザイナー指摘)。 [design 差異]
              8080 は独自 sparkle SVG を残しているが、 デザイナー判断で実装側は
              アイコン無しに倒す。 これは 2026-08-13 の「虫めがねは『検索』と
              誤読されるので FaCalendarCheckRegular に寄せる」 という判断を
              置き換えるもの (文言が「類似日から選択」 になり、 アイコン無しでも
              誤読の余地が無くなったため)。 */}
          <TertiaryButton onClick={onSimilarDaySearchOpen}>
            {t('page:demandForecastDaily.toolbar.similarDaySearch')}
          </TertiaryButton>
          {/* 「確定履歴から選択」ボタン (design 決定 2026-07-15、design
              demand/forecast-today.html:1021 #dft-histimport-btn)。 文言は 8080 の
              「確定履歴から選択」に統一した (2026-08-25 デザイナー指摘)。 この導線は
              編集中の需要想定を上書きせず**参照列を追加する**もの (dialog の
              `referenceHint` 参照) なので、「取込」より「選択」が実態に合う。 (旧: design
              demand/forecast-today.html:1021 #dft-histimport-btn):
              過去実績領域の右端に単体配置する (「取込:」グループ所属のラベルは
              付けない — 同 design L1020 の margin-left:auto wrap 相当。ボタン
              自体の可視文言は維持)。activeness は §5 の従来条件のまま。 */}
          <TertiaryButton
            marginLeft="auto"
            disabled={!importActions.canImport}
            onClick={importActions.onConfirmRecordImportOpen}
          >
            {t('page:demandForecastDaily.confirmRecordImport.buttonLabel')}
          </TertiaryButton>
          {/* design 実測 (8080 forecast-today.html): クリア = `btn btn-text btn-sm` = 32px */}
          <TextButton onClick={pastActual.onClear} size="sm">
            {t('page:demandForecastDaily.toolbar.clearButton')}
          </TextButton>
        </Flex>
      </HStack>
    </Flex>
  )
}
